export type EmailErrorCode =
  | 'required'
  | 'invalid_format'
  | 'too_long_total'
  | 'too_long_local'
  | 'dot_sequence'
  | 'dot_edge'
  | 'invalid_domain_label'
  | 'domain_missing_tld';

export interface EmailValidationError {
  code: EmailErrorCode;
}

export interface EmailValidationOptions {
  required?: boolean; // default false for Primary Email
  maxTotalLength?: number; // default 254
  maxLocalLength?: number; // default 64
}

export interface EmailValidationResult {
  isValid: boolean;
  normalized: string | null; // local as typed, domain lowercased
  errors: EmailValidationError[];
}

const INVISIBLE_CHARS_REGEX = /[\u200B-\u200D\uFEFF]/g; // zero-width chars

// Basic, safe subset email validator for frontend usage only
export function validateEmail(
  rawValue: string,
  options: EmailValidationOptions = {}
): EmailValidationResult {
  const { required = false, maxTotalLength = 254, maxLocalLength = 64 } = options;

  let value = (rawValue ?? '').replace(/^mailto:/i, '');
  value = value.replace(INVISIBLE_CHARS_REGEX, '').trim();

  if (value.length === 0) {
    if (required) {
      return { isValid: false, normalized: null, errors: [{ code: 'required' }] };
    }
    return { isValid: true, normalized: null, errors: [] };
  }

  // Quick length sanity check first
  if (value.length > maxTotalLength) {
    return {
      isValid: false,
      normalized: null,
      errors: [{ code: 'too_long_total' }],
    };
  }

  const atParts = value.split('@');
  if (atParts.length !== 2) {
    return {
      isValid: false,
      normalized: null,
      errors: [{ code: 'invalid_format' }],
    };
  }

  const [local, domainRaw] = atParts;
  const domain = domainRaw.toLowerCase();

  // Local part checks
  if (local.length === 0 || local.length > maxLocalLength) {
    return {
      isValid: false,
      normalized: null,
      errors: [{ code: local.length === 0 ? 'invalid_format' : 'too_long_local' }],
    };
  }

  if (local.startsWith('.') || local.endsWith('.')) {
    return { isValid: false, normalized: null, errors: [{ code: 'dot_edge' }] };
  }
  if (local.includes('..')) {
    return {
      isValid: false,
      normalized: null,
      errors: [{ code: 'dot_sequence' }],
    };
  }
  // Allowed ASCII characters in local part: letters, digits, dot, underscore, plus, hyphen
  if (!/^[A-Za-z0-9._+-]+$/.test(local)) {
    return {
      isValid: false,
      normalized: null,
      errors: [{ code: 'invalid_format' }],
    };
  }

  // Domain checks: must contain at least one dot
  if (!domain.includes('.')) {
    return {
      isValid: false,
      normalized: null,
      errors: [{ code: 'domain_missing_tld' }],
    };
  }

  const labels = domain.split('.');
  for (const label of labels) {
    // label 1..63, only a-z 0-9 - , cannot start/end with '-'
    if (
      label.length === 0 ||
      label.length > 63 ||
      !/^[a-z0-9-]+$/.test(label) ||
      label.startsWith('-') ||
      label.endsWith('-')
    ) {
      return {
        isValid: false,
        normalized: null,
        errors: [{ code: 'invalid_domain_label' }],
      };
    }
  }

  // If everything passes, return normalized with lowercased domain
  const normalized = `${local}@${domain}`;
  return { isValid: true, normalized, errors: [] };
}

export default validateEmail;
